import { useTranslation } from 'react-i18next';
import React, { ChangeEvent, FormEvent, useContext, useRef } from 'react';
import { ServerErrors } from 'dash-common';
import {
  Form,
  FormContext,
  FormErrorTypes,
  Flex,
  Button,
  toast,
  InputPassword,
  useServerResponseTranslation,
} from 'dash-ui';

import { useUpdatePasswordMutation } from 'gql/generated.graphql';

import useValidation from 'pages/ProfilePage/components/ProfileSettings/components/UpdatePasswordForm/hooks/useValidation';
import { FormFields } from 'pages/ProfilePage/components/ProfileSettings/components/UpdatePasswordForm/constants/constants';

const UpdatePasswordForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const {
    formState,
    formOnSubmit,
    formOnError,
    errorHandlers,
    formErrors,
    stateHandlers,
  } = useContext(FormContext);
  const valid = useValidation();
  const translateErrorMessage = useServerResponseTranslation();

  const [updatePassword, { loading }] = useUpdatePasswordMutation();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;
    if (
      !valid(FormFields.Password, formValues.password) ||
      !valid(FormFields.NewPassword, formValues.newPassword) ||
      !valid(FormFields.ConfirmPassword, formValues.confirmPassword)
    ) {
      return null;
    }

    const input = {
      oldPassword: formState.password,
      newPassword: formState.newPassword,
    };

    return updatePassword({
      variables: input,
      onCompleted: () => {
        toast.success(t('pages:profilePage.toasts.updatedPassword'));
        onSuccess();
      },
      onError: (error: { message: string }) => {
        if (error.message === ServerErrors.INCORRECT_PASSWORD) {
          errorHandlers[FormFields.Password]({
            type: FormErrorTypes.Invalid,
            message: t(
              `common:brokerPortalServerErrors:${ServerErrors.INCORRECT_PASSWORD}`
            ),
          });
          return;
        }
        formOnError(
          translateErrorMessage(error, ['common:brokerPortalServerErrors'])
        );
      },
    });
  };

  const onTextChanged =
    (type: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      stateHandlers[type](value);
      valid(type, value);
    };

  return (
    <Form ref={formRef} onSubmit={onSubmit} w="100%" maxWidth={332}>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.oldPassword`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.password` || ''}
          onChange={onTextChanged(FormFields.Password)}
          $errorType={formErrors[FormFields.Password]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Password]?.type}>
          {formErrors[FormFields.Password]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.password`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.newPassword` || ''}
          onChange={onTextChanged(FormFields.NewPassword)}
          $errorType={formErrors[FormFields.NewPassword]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.NewPassword]?.type}>
          {formErrors[FormFields.NewPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Form.Label>{t`labels.confirmPassword`}</Form.Label>
        <InputPassword
          w="100%"
          placeholder={t`placeholders.confirmPassword` || ''}
          onChange={onTextChanged(FormFields.ConfirmPassword)}
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        />
        <Form.Feedback
          $errorType={formErrors[FormFields.ConfirmPassword]?.type}
        >
          {formErrors[FormFields.ConfirmPassword]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={4}>
        <Button w="100%" type="submit" disabled={loading} isLoading={loading}>
          {t('pages:profilePage:settings:updatePassword')}
        </Button>
      </Form.Group>
      <Flex mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Flex>
    </Form>
  );
};

export default UpdatePasswordForm;
